/**
 * UploadCenter — global, in-memory upload queue (Instagram-style background publish).
 *
 * Contract (founder-approved, Jun 2026):
 *   · Callers (StoryUploadDialog, UploadContentDialog, etc.) call `enqueue({ taskFn, … })`
 *     and immediately close their own UI. The upload then runs in the
 *     background while the user keeps navigating.
 *   · State machine per item:
 *       preparing → uploading → processing → published   (happy path)
 *                                          ↘ error       (any throw)
 *   · Items keep their `taskFn` + `draft` in memory so a failed upload can
 *     be retried without re-opening the original dialog.
 *   · Successful items auto-disappear after AUTOPRUNE_MS. Errored items
 *     stay until the user retries or dismisses them.
 *   · Persistence: **none** (purely in-memory). A page refresh aborts any
 *     in-flight upload — same behaviour as Instagram when you kill the app
 *     mid-publish. We explicitly chose this over localStorage because
 *     storing pending video Blobs (>100 MB) in localStorage causes browser
 *     freezes and QuotaExceededError on iOS Safari.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import { tr } from '@/contexts/LanguageContext';
import { friendlyError } from '@/lib/apiError';

const UploadCenterContext = createContext(null);

export const UPLOAD_STATES = {
  PREPARING: 'preparing',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  PUBLISHED: 'published',
  ERROR: 'error',
};

export const STATE_LABEL_ES = {
  [UPLOAD_STATES.PREPARING]: 'Preparando…',
  [UPLOAD_STATES.UPLOADING]: 'Subiendo…',
  [UPLOAD_STATES.PROCESSING]: 'Procesando…',
  [UPLOAD_STATES.PUBLISHED]: tr("Publicado"),
  [UPLOAD_STATES.ERROR]: tr("Error"),
};

// Iter 144 — context-aware state label resolution. Returns the
// item-supplied `processingLabel` override (used by Story video
// uploads to surface "Procesando video en alta definición (hasta
// 60s)…") and falls back to the generic STATE_LABEL_ES otherwise.
// Centralised here so any future per-state override slots into the
// same point without touching the banner UI.
export const labelForState = (item) => {
  if (!item) return '';
  if (item.state === UPLOAD_STATES.PROCESSING && item.processingLabel) {
    return item.processingLabel;
  }
  return STATE_LABEL_ES[item.state] || '';
};

const AUTOPRUNE_MS = 3500;     // success items vanish after 3.5s
const ID_PREFIX = 'up_';

const newId = () =>
  `${ID_PREFIX}${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const UploadCenterProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const runTask = useCallback(async (item) => {
    try {
      updateItem(item.id, {
        state: UPLOAD_STATES.PREPARING, progress: 0, error: null,
      });
      const result = await item.taskFn({
        onProgress: (pct) => updateItem(item.id, {
          progress: Math.min(95, Math.max(0, Math.round(pct || 0))),
          state: UPLOAD_STATES.UPLOADING,
        }),
        onState: (next) => updateItem(item.id, { state: next }),
      });
      updateItem(item.id, {
        state: UPLOAD_STATES.PUBLISHED, progress: 100, result,
      });
      // Fire-and-forget consumer hook (e.g., refresh feed)
      try { item.onSuccess?.(result); } catch { /* noop */ }
      // Auto-prune after the success animation has played
      setTimeout(() => dismiss(item.id), AUTOPRUNE_MS);
    } catch (err) {
      // Mensaje legible, sin códigos HTTP (oct-2026): primero el detalle del backend en español.
      const msg = friendlyError(err, err?.message?.replace(/\s*\(HTTP \d+\)$/, '') || tr("Error al publicar."));
      updateItem(item.id, {
        state: UPLOAD_STATES.ERROR,
        error: msg,
      });
      try { item.onError?.(err); } catch { /* noop */ }
    }
  }, [updateItem, dismiss]);

  /**
   * Push an upload onto the queue. Returns the item id.
   *
   * @param {Object} opts
   *   kind           'post' | 'reel' | 'story'         (drives label + icon)
   *   label          Human-readable Spanish label
   *   taskFn         async ({onProgress, onState}) => result
   *   draft          Anything the caller wants to keep around for retry
   *   onSuccess      result => void
   *   onError        err => void
   */
  const enqueue = useCallback((opts) => {
    if (!opts || typeof opts.taskFn !== 'function') {
      throw new Error('UploadCenter: enqueue requires a taskFn');
    }
    const id = opts.id || newId();
    const item = {
      id,
      kind: opts.kind || 'post',
      label: opts.label || tr("Publicación"),
      taskFn: opts.taskFn,
      draft: opts.draft || null,
      onSuccess: opts.onSuccess,
      onError: opts.onError,
      state: UPLOAD_STATES.PREPARING,
      progress: 0,
      error: null,
      createdAt: Date.now(),
    };
    setItems((prev) => [...prev, item]);
    // Defer to next tick so React commits the item before the async task starts.
    setTimeout(() => runTask(item), 0);
    return id;
  }, [runTask]);

  const retry = useCallback((id) => {
    // Read the latest item from the functional setItems snapshot to avoid
    // ref-during-render anti-patterns. We just need the original taskFn.
    setItems((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target) setTimeout(() => runTask(target), 0);
      return prev;
    });
  }, [runTask]);

  const value = useMemo(() => ({
    items,
    enqueue,
    retry,
    dismiss,
  }), [items, enqueue, retry, dismiss]);

  return (
    <UploadCenterContext.Provider value={value}>
      {children}
    </UploadCenterContext.Provider>
  );
};

export const useUploadCenter = () => {
  const ctx = useContext(UploadCenterContext);
  if (!ctx) {
    throw new Error('useUploadCenter must be used inside <UploadCenterProvider>');
  }
  return ctx;
};

/* ── Selectors / derived helpers ─────────────────────────────────────── */

/** Aggregate progress across all active (non-error, non-published) items. */
export const aggregateProgress = (items) => {
  const active = items.filter((i) => (
    i.state === UPLOAD_STATES.PREPARING
    || i.state === UPLOAD_STATES.UPLOADING
    || i.state === UPLOAD_STATES.PROCESSING
  ));
  if (active.length === 0) return null;
  const total = active.reduce((acc, i) => acc + (i.progress || 0), 0);
  return Math.round(total / active.length);
};

/** True iff there's at least one item still running. */
export const hasActiveUploads = (items) =>
  items.some((i) => (
    i.state === UPLOAD_STATES.PREPARING
    || i.state === UPLOAD_STATES.UPLOADING
    || i.state === UPLOAD_STATES.PROCESSING
  ));
